type InvoiceNumberSequence = {
  canonicalDigits: string;
  formatRank: number;
};

type CompareInvoiceNumbersInput = {
  firstInvoiceNumber: string;
  secondInvoiceNumber: string;
};

type SortRowsByInvoiceNumberInput<Row> = {
  direction: "asc" | "desc";
  getInvoiceNumber: (row: Row) => string;
  rows: readonly Row[];
};

const normalizeDigits = (digits: string): string =>
  digits.replace(/^0+(?=\d)/, "");

const getPositiveInvoiceNumberSequence = ({
  digits,
  formatRank,
}: {
  digits: string;
  formatRank: number;
}): InvoiceNumberSequence | null => {
  const canonicalDigits = normalizeDigits(digits);

  return canonicalDigits === "0"
    ? null
    : { canonicalDigits, formatRank };
};

const getInvoiceNumberSequence = (
  invoiceNumber: string
): InvoiceNumberSequence | null => {
  const trimmedInvoiceNumber = invoiceNumber.trim();
  const plainNumberMatch = /^(\d+)$/.exec(trimmedInvoiceNumber);

  if (plainNumberMatch) {
    return getPositiveInvoiceNumberSequence({
      digits: plainNumberMatch[1],
      formatRank: 0,
    });
  }

  const prefixedNumberMatch = /^INV-(\d+)$/.exec(trimmedInvoiceNumber);

  return prefixedNumberMatch
    ? getPositiveInvoiceNumberSequence({
        digits: prefixedNumberMatch[1],
        formatRank: 1,
      })
    : null;
};

const compareCanonicalDigits = (
  firstDigits: string,
  secondDigits: string
): number => {
  if (firstDigits.length !== secondDigits.length) {
    return firstDigits.length - secondDigits.length;
  }

  return firstDigits.localeCompare(secondDigits);
};

export const compareInvoiceNumbers = ({
  firstInvoiceNumber,
  secondInvoiceNumber,
}: CompareInvoiceNumbersInput): number => {
  const firstSequence = getInvoiceNumberSequence(firstInvoiceNumber);
  const secondSequence = getInvoiceNumberSequence(secondInvoiceNumber);

  if (firstSequence && secondSequence) {
    const numericComparison = compareCanonicalDigits(
      firstSequence.canonicalDigits,
      secondSequence.canonicalDigits
    );

    if (numericComparison !== 0) {
      return numericComparison;
    }

    if (firstSequence.formatRank !== secondSequence.formatRank) {
      return firstSequence.formatRank - secondSequence.formatRank;
    }
  } else if (firstSequence || secondSequence) {
    return firstSequence ? -1 : 1;
  }

  return firstInvoiceNumber.localeCompare(secondInvoiceNumber);
};

export const sortRowsByInvoiceNumber = <Row>({
  direction,
  getInvoiceNumber,
  rows,
}: SortRowsByInvoiceNumberInput<Row>): Row[] => {
  const directionFactor = direction === "asc" ? 1 : -1;

  return rows
    .map((row, originalIndex) => ({ originalIndex, row }))
    .sort((first, second) => {
      const comparison =
        compareInvoiceNumbers({
          firstInvoiceNumber: getInvoiceNumber(first.row),
          secondInvoiceNumber: getInvoiceNumber(second.row),
        }) * directionFactor;

      return comparison || first.originalIndex - second.originalIndex;
    })
    .map(({ row }) => row);
};
